import styled from 'styled-components'
import FullScreenOverlay from '../../../shared_components/Library/FullScreenOverlay'
import {
  A,
  H1,
  H3,
  P,
  Span,
} from '../../../shared_components/Library/Typeography'
import { Box } from '../../../shared_components/UI/Box'
import { questBackdrop } from './QuestComplete'
import { Prizes, ScrollableModalButtonProps, TopNavBar } from './About'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setBottomNavButton } from '../../../modules/miscUi/actions'
import {
  useAcknowledgeSeasonEndMutation,
  useLazyGetCurrentSeasonProgressQuery,
  useLazyGetCurrentSeasonQuery,
} from '../../../modules/seasons/api'
import {
  selectClaimPrizeTxHash,
  selectCurrentSeason,
  selectCurrentSeasonProgress,
  selectPastSeasonProgress,
  selectPreviousSeasonFocusingOn,
} from '../../../modules/seasons/selectors'
import {
  setCurrentSeason,
  setCurrentSeasonGraduatedMemecoins,
  setCurrentSeasonProgress,
  setClaimPrizeTxHash,
} from '../../../modules/seasons/slice'
import { toast } from 'react-toastify'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { Chip } from '../../../shared_components/Library/Chip'
import { useGetEthersContractInstanceWithSigner } from '../../Memecoins/useTokenContractValues'
import Separator from '../../../shared_components/Library/Separator'
import { useShouldShowForThreshold } from '../../../theme'
import { useHistory } from 'react-router-dom'

export default function SeasonComplete({
  isShowingPrevious,
}: {
  isShowingPrevious?: boolean
}) {
  const previousSeasonFocusingOn = useSelector(selectPreviousSeasonFocusingOn)
  const currentSeason = useSelector(selectCurrentSeason)
  const season = previousSeasonFocusingOn || currentSeason
  const thisSeasonProgress = useSelector(selectCurrentSeasonProgress)
  const previousSeasonProgresses = useSelector(selectPastSeasonProgress)
  const progress = previousSeasonFocusingOn
    ? previousSeasonProgresses[season.id]
    : thisSeasonProgress
  const didWin = !!progress?.hasWon
  const claimPrizeTxHash = progress?.claimPrizeTxHash

  const dispatch = useDispatch()
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  useEffect(() => {
    dispatch(
      setBottomNavButton(
        didWin ? 'claim_season_prizes' : 'acknowledge_season_end',
      ),
    )
    return () => {
      dispatch(setBottomNavButton(null))
    }
  }, [dispatch, didWin])

  return (
    <FullScreenOverlay dismissable={isShowingPrevious} showHeader>
      <>
        {isShowingPrevious && isAtLeastMediumDisplaySize && <Box padV={16} />}
        {didWin ? (
          <SeasonWon
            claimPrizeTxHash={claimPrizeTxHash}
            seasonName={isShowingPrevious ? season.name : undefined}
          />
        ) : (
          <SeasonLost />
        )}
      </>
    </FullScreenOverlay>
  )
}

function SeasonWon({
  claimPrizeTxHash,
  seasonName,
}: {
  claimPrizeTxHash?: string
  seasonName?: string
}) {
  const { name } = useSelector(selectCurrentSeason)
  const hasClaimed = !!claimPrizeTxHash
  return (
    <WinContainer dc hasClaimed={hasClaimed}>
      <img
        src="/img/season-winner.svg"
        alt="winner-banner"
        className="banner"
      />
      <Box c dc g={8} className="info-container">
        <H1 s="3XL">⚡️ JACKPOT! ⚡️</H1>
        <H3 s="L">{!!seasonName ? seasonName : name} has ended</H3>
        <P ta="center" s="M">
          It’s your lucky day! Your tickets have secured this week's big win!
          Keep up the momentum and join next week's raffle for more chances to
          win!
        </P>
        <Box padV={24}>
          <Separator />
        </Box>
        <Box dc c padB="s" className="prizes">
          <Prizes
            isWinnerScreen
            showPeriodGraduatedCoins
            hasClaimed={hasClaimed}
          />
        </Box>
      </Box>
    </WinContainer>
  )
}

const WinContainer = styled(Box)<{ hasClaimed: boolean }>`
  gap: var(--Spacing-Padding-L);
  img.banner {
    width: 100%;
  }
  @media screen and (max-width: ${(props) =>
      props.theme.breakpoints.medium}px) {
    .info-container {
      padding: var(--Index-Grids-Gutters, 32px);
      padding-top: 0;
    }
    .prizes {
      padding-bottom: calc(
        69px + 420px - 400px
          ${({ hasClaimed }) => (!hasClaimed ? '' : '+ 32px')}
      );
    }
  }
`

function SeasonLost() {
  const { name: seasonName } = useSelector(selectCurrentSeason)
  return (
    <Box dc>
      <SadZapepe c>
        <img src="/img/sad-zapepe.svg" />
      </SadZapepe>
      <TitleContainer dc c g={12} padT={32}>
        <H1 s="4XL">⛔️ UNLUCKY 👎</H1>
        <H3 s="XL" fw="regular">
          {seasonName}
        </H3>
        <Chip>Ended</Chip>
      </TitleContainer>
      <Box c padT={24} padB={69 - 37}>
        <Span ta="center">
          Not a win this time, but keep going! Every ticket counts, and next
          week’s prize could be yours!
        </Span>
      </Box>
    </Box>
  )
}

const TitleContainer = styled(Box)`
  h1,
  h3 {
    text-align: center;
    margin: auto;
  }
  ${Chip} {
    background: linear-gradient(
        0deg,
        var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
        var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
      ),
      var(--surface-primary, #080808);
  }
`

const SadZapepe = styled(Box)`
  ${questBackdrop}
  min-height: calc(420px - 69px);
  img {
    max-width: 69%;
  }
`

export function ClaimSeasonPrizesButton({
  continueScrolling,
  moreToScroll,
}: ScrollableModalButtonProps) {
  const currentUserSeasonProgress = useSelector(selectCurrentSeasonProgress)
  const currentSeason = useSelector(selectCurrentSeason)
  const previousSeasonFocusingOn = useSelector(selectPreviousSeasonFocusingOn)
  const isShowingPrevious = !!previousSeasonFocusingOn

  const { id: seasonId, contractAddress } = isShowingPrevious
    ? previousSeasonFocusingOn
    : currentSeason

  const history = useHistory()

  const [isClaimingPrize, setIsClaimingPrize] = useState(false)

  const [acknowledgeSeasonEnd, { isLoading: isLoadingAcknowledge, error }] =
    useAcknowledgeSeasonEndMutation()

  const [updatedSeasonRequest] = useLazyGetCurrentSeasonQuery()
  const [updatedSeasonProgressRequest] = useLazyGetCurrentSeasonProgressQuery()
  const dispatch = useDispatch()

  const claimPrizeTxHash = useSelector(selectClaimPrizeTxHash)
  const seasonContractEthersInstance = useGetEthersContractInstanceWithSigner(
    'Season',
    contractAddress,
  )

  const hasClaimedPrizes = !!claimPrizeTxHash

  const handleClick = useCallback(async () => {
    if (isShowingPrevious && hasClaimedPrizes) {
      return history.goBack()
    }
    if (hasClaimedPrizes) {
      const success = await acknowledgeSeasonEnd({ seasonId }).unwrap()
      console.log({ success })
      const updatedSeason = await updatedSeasonRequest({}).unwrap()
      const updatedSeasonProgress = await updatedSeasonProgressRequest(
        {},
      ).unwrap()
      dispatch(setCurrentSeason(updatedSeason))
      dispatch(setCurrentSeasonProgress(updatedSeasonProgress))
      dispatch(setCurrentSeasonGraduatedMemecoins([]))
      dispatch(setClaimPrizeTxHash(null))
      return
    }
    try {
      setIsClaimingPrize(true)

      const gasEstimate =
        await seasonContractEthersInstance['claimPrizes'].estimateGas()
      if (!gasEstimate) throw new Error('Gas estimate failed')

      const tx = await seasonContractEthersInstance.claimPrizes({
        gasLimit: gasEstimate,
      })
      if (!tx.hash) throw new Error('Tx failed')
      dispatch(setClaimPrizeTxHash(tx.hash))
      dispatch(
        setCurrentSeasonProgress({
          ...currentUserSeasonProgress,
          claimPrizeTxHash,
        }),
      )
      toast.success('Prizes claimed!')
    } catch (error) {
      console.error(error)
      toast.error('Error claiming prizes')
    } finally {
      setIsClaimingPrize(false)
    }
  }, [seasonContractEthersInstance, hasClaimedPrizes, isShowingPrevious])

  if (error) {
    toast.error('Failed to proceed to season')
  }

  const isLoading = isLoadingAcknowledge
  const canScroll = continueScrolling && moreToScroll

  return (
    <Box dc g={16} id="claim-season-prizes">
      {!!claimPrizeTxHash && (
        <Box ac jsb>
          <ClaimedTag />
          <A
            s="S"
            target="_blank"
            href={`https://basescan.org/tx/${claimPrizeTxHash}`}
            td="underline"
          >
            View on Basescan
          </A>
        </Box>
      )}
      <ThemedButton
        $styleType="primary"
        w="100%"
        onClick={
          canScroll && !isShowingPrevious ? continueScrolling : handleClick
        }
        disabled={isLoading}
      >
        {isShowingPrevious && !!claimPrizeTxHash ? (
          'Go back'
        ) : canScroll ? (
          'Continue'
        ) : isLoading || isClaimingPrize ? (
          <AnimatedLoader />
        ) : !!claimPrizeTxHash ? (
          'Continue to next season'
        ) : (
          'Claim prizes'
        )}
      </ThemedButton>
    </Box>
  )
}

export function ClaimedTag() {
  return (
    <Box ac g={2}>
      <LittleGreenCheck />
      <Span s="S">Claimed</Span>{' '}
    </Box>
  )
}

export const LittleGreenCheck = () => (
  <img
    src="/img/icons/green-check.svg"
    alt="checkmark"
    style={{ width: 16, height: 16 }}
  />
)

export function AcknowledgeSeasonLossButton({
  continueScrolling,
  moreToScroll,
}: ScrollableModalButtonProps) {
  const { id: seasonId } = useSelector(selectCurrentSeason)

  const dispatch = useDispatch()

  const [updatedSeasonProgressRequest] = useLazyGetCurrentSeasonProgressQuery()
  const [updatedSeasonRequest] = useLazyGetCurrentSeasonQuery()
  const [acknowledgeSeasonEnd, { isLoading: isLoadingAcknowledge, error }] =
    useAcknowledgeSeasonEndMutation()

  const handleClick = async () => {
    const success = await acknowledgeSeasonEnd({ seasonId }).unwrap()
    console.log({ success })
    const updatedSeason = await updatedSeasonRequest({}).unwrap()
    const updatedSeasonProgress = await updatedSeasonProgressRequest(
      {},
    ).unwrap()
    dispatch(setCurrentSeason(updatedSeason))
    dispatch(setCurrentSeasonProgress(updatedSeasonProgress))
    dispatch(setCurrentSeasonGraduatedMemecoins([]))
    dispatch(setClaimPrizeTxHash(null))
  }

  if (error) {
    toast.error('Failed to proceed to season')
  }

  const isLoading = isLoadingAcknowledge
  const canScroll = continueScrolling && moreToScroll

  return (
    <ThemedButton
      $styleType="primary"
      w="100%"
      onClick={canScroll ? continueScrolling : handleClick}
      disabled={isLoading}
    >
      {canScroll ? (
        'Continue'
      ) : isLoading ? (
        <AnimatedLoader />
      ) : (
        `Continue to new season`
      )}
    </ThemedButton>
  )
}
